import React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { createStyles, palette, spacing } from "../../../styles/theme";
import PricingRangeRow from "./PricingRangeRow";
import SpecsJsonField from "./SpecsJsonField";
import SpecsNumberField from "./SpecsNumberField";
import { readArray, readObject } from "./utils";

const styles = createStyles({
  currencyPaper: {
    p: spacing.groupingDesktop,
    border: `1px solid ${palette.grey.lightGrey}`,
    bgcolor: palette.grey.lightBackground,
  },
});

function normalizeCurrencyEntries(value: unknown) {
  return Object.entries(readObject(value)).map(
    ([currencyCode, rawCurrency]) => {
      const source = readObject(rawCurrency);
      const distributor = readObject(source.distributor);
      const pricingRange = readArray(source.pricingRange).map((entry) =>
        readObject(entry)
      );
      const pricePerSize = readArray(source.pricePerSize);
      const {
        distributor: _distributor,
        pricingRange: _pricingRange,
        pricePerSize: _pricePerSize,
        ...extra
      } = source;

      return {
        currencyCode,
        carbonNeutralityFee:
          typeof source.carbonNeutralityFee === "number"
            ? source.carbonNeutralityFee
            : null,
        priceOfExtraCredit:
          typeof source.priceOfExtraCredit === "number"
            ? source.priceOfExtraCredit
            : null,
        priceForExtraSize:
          typeof source.priceForExtraSize === "number"
            ? source.priceForExtraSize
            : null,
        recommendedMargin:
          typeof distributor.recommendedMargin === "number"
            ? distributor.recommendedMargin
            : null,
        pricingRange,
        pricePerSize,
        extra,
      };
    }
  );
}

function buildCurrencyValue(
  entry: ReturnType<typeof normalizeCurrencyEntries>[number]
) {
  return {
    ...entry.extra,
    ...(entry.carbonNeutralityFee === null
      ? {}
      : { carbonNeutralityFee: entry.carbonNeutralityFee }),
    ...(entry.priceOfExtraCredit === null
      ? {}
      : { priceOfExtraCredit: entry.priceOfExtraCredit }),
    ...(entry.priceForExtraSize === null
      ? {}
      : { priceForExtraSize: entry.priceForExtraSize }),
    ...(entry.recommendedMargin === null
      ? {}
      : {
          distributor: {
            recommendedMargin: entry.recommendedMargin,
          },
        }),
    ...(entry.pricingRange.length === 0
      ? {}
      : { pricingRange: entry.pricingRange }),
    ...(entry.pricePerSize.length === 0
      ? {}
      : { pricePerSize: entry.pricePerSize }),
  };
}

function buildPricingCurrencyValue(
  entries: ReturnType<typeof normalizeCurrencyEntries>
) {
  return Object.fromEntries(
    entries.map((entry) => [entry.currencyCode, buildCurrencyValue(entry)])
  );
}

export default function SpecsPricingCurrencyField({
  label,
  value,
  debugPath,
  clickToEdit,
  onChange,
}: {
  label: string;
  value: unknown;
  debugPath?: string;
  clickToEdit?: boolean;
  onChange: (nextValue: unknown) => void;
}) {
  const entries = normalizeCurrencyEntries(value);

  const updateEntries = React.useCallback(
    (
      updater: (
        current: ReturnType<typeof normalizeCurrencyEntries>
      ) => ReturnType<typeof normalizeCurrencyEntries>
    ) => {
      onChange(
        buildPricingCurrencyValue(updater(normalizeCurrencyEntries(value)))
      );
    },
    [onChange, value]
  );

  const addCurrency = (currencyCode: string) => {
    updateEntries((current) => {
      if (current.some((entry) => entry.currencyCode === currencyCode)) {
        return current;
      }
      return [
        ...current,
        {
          currencyCode,
          carbonNeutralityFee: null,
          priceOfExtraCredit: null,
          priceForExtraSize: null,
          recommendedMargin: null,
          pricingRange: [],
          pricePerSize: [],
          extra: {},
        },
      ];
    });
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">{label}</Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          size="small"
          onClick={() => addCurrency("cad")}
        >
          Add CAD
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => addCurrency("usd")}
        >
          Add USD
        </Button>
      </Stack>

      {entries.map((entry) => (
        <Paper
          key={entry.currencyCode}
          variant="outlined"
          sx={styles.currencyPaper}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">
                {entry.currencyCode.toUpperCase()}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={() =>
                  updateEntries((current) =>
                    current.filter(
                      (candidate) =>
                        candidate.currencyCode !== entry.currencyCode
                    )
                  )
                }
              >
                Remove
              </Button>
            </Stack>

            <SpecsNumberField
              label="Carbon Neutrality Fee"
              value={entry.carbonNeutralityFee}
              allowNull
              debugPath={
                debugPath
                  ? `${debugPath}.${entry.currencyCode}.carbonNeutralityFee`
                  : undefined
              }
              clickToEdit={clickToEdit}
              onChange={(nextValue) =>
                updateEntries((current) =>
                  current.map((candidate) =>
                    candidate.currencyCode === entry.currencyCode
                      ? { ...candidate, carbonNeutralityFee: nextValue }
                      : candidate
                  )
                )
              }
            />
            <SpecsNumberField
              label="Price Of Extra Credit"
              value={entry.priceOfExtraCredit}
              allowNull
              debugPath={
                debugPath
                  ? `${debugPath}.${entry.currencyCode}.priceOfExtraCredit`
                  : undefined
              }
              clickToEdit={clickToEdit}
              onChange={(nextValue) =>
                updateEntries((current) =>
                  current.map((candidate) =>
                    candidate.currencyCode === entry.currencyCode
                      ? { ...candidate, priceOfExtraCredit: nextValue }
                      : candidate
                  )
                )
              }
            />
            <SpecsNumberField
              label="Price For Extra Size"
              value={entry.priceForExtraSize}
              allowNull
              debugPath={
                debugPath
                  ? `${debugPath}.${entry.currencyCode}.priceForExtraSize`
                  : undefined
              }
              clickToEdit={clickToEdit}
              onChange={(nextValue) =>
                updateEntries((current) =>
                  current.map((candidate) =>
                    candidate.currencyCode === entry.currencyCode
                      ? { ...candidate, priceForExtraSize: nextValue }
                      : candidate
                  )
                )
              }
            />
            <SpecsNumberField
              label="Distributor Recommended Margin"
              value={entry.recommendedMargin}
              allowNull
              debugPath={
                debugPath
                  ? `${debugPath}.${entry.currencyCode}.distributor.recommendedMargin`
                  : undefined
              }
              clickToEdit={clickToEdit}
              onChange={(nextValue) =>
                updateEntries((current) =>
                  current.map((candidate) =>
                    candidate.currencyCode === entry.currencyCode
                      ? { ...candidate, recommendedMargin: nextValue }
                      : candidate
                  )
                )
              }
            />

            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">Pricing Ranges</Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    updateEntries((current) =>
                      current.map((candidate) =>
                        candidate.currencyCode === entry.currencyCode
                          ? {
                              ...candidate,
                              pricingRange: [...candidate.pricingRange, {}],
                            }
                          : candidate
                      )
                    )
                  }
                >
                  Add Range
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                {entry.pricingRange.map((rangeEntry, index) => (
                  <PricingRangeRow
                    key={`${entry.currencyCode}-${index}`}
                    entry={rangeEntry}
                    index={index}
                    clickToEdit={clickToEdit}
                    debugPath={
                      debugPath
                        ? `${debugPath}.${entry.currencyCode}.pricingRange.${index}`
                        : undefined
                    }
                    onChange={(nextRangeEntry) =>
                      updateEntries((current) =>
                        current.map((candidate) =>
                          candidate.currencyCode === entry.currencyCode
                            ? {
                                ...candidate,
                                pricingRange: candidate.pricingRange.map(
                                  (existingRangeEntry, existingIndex) =>
                                    existingIndex === index
                                      ? nextRangeEntry
                                      : existingRangeEntry
                                ),
                              }
                            : candidate
                        )
                      )
                    }
                    onRemove={() =>
                      updateEntries((current) =>
                        current.map((candidate) =>
                          candidate.currencyCode === entry.currencyCode
                            ? {
                                ...candidate,
                                pricingRange: candidate.pricingRange.filter(
                                  (_rangeEntry, existingIndex) =>
                                    existingIndex !== index
                                ),
                              }
                            : candidate
                        )
                      )
                    }
                  />
                ))}
              </Stack>
            </Stack>

            <SpecsJsonField
              label="Price Per Size"
              value={entry.pricePerSize}
              minRows={6}
              debugPath={
                debugPath
                  ? `${debugPath}.${entry.currencyCode}.pricePerSize`
                  : undefined
              }
              clickToEdit={clickToEdit}
              onChange={(nextValue) =>
                updateEntries((current) =>
                  current.map((candidate) =>
                    candidate.currencyCode === entry.currencyCode
                      ? {
                          ...candidate,
                          pricePerSize: Array.isArray(nextValue)
                            ? nextValue
                            : [],
                        }
                      : candidate
                  )
                )
              }
            />

            <SpecsJsonField
              label="Currency Extra JSON"
              value={entry.extra}
              minRows={4}
              debugPath={
                debugPath ? `${debugPath}.${entry.currencyCode}` : undefined
              }
              clickToEdit={clickToEdit}
              onChange={(nextValue) =>
                updateEntries((current) =>
                  current.map((candidate) =>
                    candidate.currencyCode === entry.currencyCode
                      ? {
                          ...candidate,
                          extra: readObject(nextValue),
                        }
                      : candidate
                  )
                )
              }
            />
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
